import { TagsInput } from '@ark-ui/react/tags-input';
import { EMAIL_PATTERN } from '@consigli/hooks';
import { TriangleAlert, X } from 'lucide-react';
import { type FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The weight the redesign draws its lucide marks at. */
const ICON_STROKE_WIDTH = 1.75;

/**
 * What separates one address from the next, both as a key that commits the
 * draft and as the split used on paste.
 *
 * A RegExp rather than the default single comma: zag applies it to both jobs
 * (`inputValue.split(delimiter)` on paste, and `/<source>$/` against the input
 * on every keystroke), so one value covers a typed comma, a typed space, and a
 * list pasted out of Outlook with semicolons or newlines between the addresses.
 */
const DELIMITER = /[,;\s]+/;

/**
 * Whether an address may be invited.
 *
 * Both halves are the rule `features/invite-user/invite-fields.tsx` already
 * applies: a well-formed address, and an AECOM one. Inviting into the
 * organisation is an AECOM action — `util/aecom.ts` records why every entry
 * point has to agree on that, and a second spelling is how one of them drifts
 * open. The server still decides whether an invite succeeds.
 */
export const isInvitableRecipient = (email: string): boolean =>
  EMAIL_PATTERN.test(email) && email.toLowerCase().endsWith('@aecom.com');

/**
 * The `validate` zag calls, which has to answer for two different strings.
 *
 * Typed, it is handed one draft address. Pasted, it is handed the whole
 * clipboard in one piece and only splits it on the delimiter *after* the guard
 * passes — so a validator that asks whether the argument is an address refuses
 * every list of more than one, which is the case this field exists for. Both
 * paths are covered by splitting first and asking of each part.
 *
 * Empty is refused rather than waved through: `every` is true of no elements,
 * which would let a bare delimiter commit nothing and clear the input.
 */
export const areInvitableRecipients = (input: string): boolean => {
  const parts = input
    .split(DELIMITER)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

  return parts.length > 0 && parts.every(isInvitableRecipient);
};

interface InviteRecipientsFieldProps {
  disabled?: boolean;
  onChange: (recipients: string[]) => void;
  value: string[];
}

/**
 * The panel's Recipients field: a chip per address over a free-text input.
 * Figma 11930:195555, with the chip itself drawn at 11930:194479.
 *
 * Ark's TagsInput rather than `features/invite-user/multi-email-input.tsx`,
 * which is the legacy flow's and stays untouched. What Ark brings that the
 * hand-rolled one does not is the keyboard contract: arrow keys walk the chips,
 * Backspace from an empty input highlights then deletes the last one, and every
 * add, edit and delete is announced through its own live region. That is the
 * part most expensive to re-derive and easiest to get subtly wrong.
 *
 * Uncontrolled input value, controlled tag value: the draft is Ark's business
 * and the committed list is the form's.
 */
export const InviteRecipientsField: FC<InviteRecipientsFieldProps> = ({
  disabled = false,
  onChange,
  value,
}) => {
  const { t } = useTranslation();
  /*
   * Why the last attempt was refused, or null. Held here rather than derived,
   * because the input clears itself on a rejected commit and the rejected text
   * is gone by the time anything could re-derive it.
   */
  const [rejected, setRejected] = useState<string | null>(null);
  /** Focused when the control's empty area is clicked — see the Control below. */
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <TagsInput.Root
      // A pasted list becomes chips rather than one run-on string. Off by
      // default in zag, and the whole point of DELIMITER below: inviting a
      // team means pasting the addresses out of a mail client in one go.
      addOnPaste
      // Committing on blur, so an address typed and then clicked away from is
      // not silently dropped. The panel's Cancel and its close both leave
      // through a pointer, which is exactly that path.
      blurBehavior="add"
      // Ark's Root is a bare div and its Label an inline <label>, so without
      // this the label sat flush against the box with no gap at all. The frame
      // puts 8 between them — 11930:195911 over 11949:196315 — which is the
      // same 8 the Role group uses between its label and control.
      className="flex w-full flex-col gap-2"
      delimiter={DELIMITER}
      disabled={disabled}
      // The frame gives the chip no edit affordance, and an address is retyped
      // rather than corrected in place. Double-clicking one would otherwise
      // turn it into an input.
      editable={false}
      // Puts aria-invalid on the input. The red border is a class on the
      // Control rather than a data-[invalid] variant, so one expression owns
      // both the rule and what the message says.
      invalid={rejected !== null}
      onValueChange={({ value: next }) => {
        setRejected(null);
        onChange(next);
      }}
      onValueInvalid={() => setRejected(t('project-overview.invite-recipient-invalid'))}
      // Lower-cased and trimmed before anything else sees it, so Ark's own
      // duplicate check (allowDuplicates is false by default) catches
      // Jane.Smith@aecom.com against jane.smith@aecom.com — and the list the
      // form submits needs no second normalisation pass.
      sanitizeValue={(entry) => entry.trim().toLowerCase()}
      validate={({ inputValue }) => areInvitableRecipients(inputValue)}
      value={value}
    >
      <TagsInput.Label className="text-sm font-medium leading-5 text-[var(--ds-color-raw-stone-950)]">
        {t('project-overview.invite-recipients')}
      </TagsInput.Label>

      {/* 136 tall with 8 of padding, per 11949:196315 — a drop area rather
          than a one-line field, because a project invite goes to a team.

          A minimum rather than a fixed height: the frame draws the empty
          state, and a list long enough to pass 136 has to grow the box rather
          than scroll inside it or spill out of it.

          This replaces a `pb-120` read off 11930:195537, which draws the same
          field holding six chips. The two frames disagree — 120 of bottom
          padding leaves an empty field at 156 — and 11949:196315 is the one
          that specifies the field rather than showing it in use.

          The click handler is what stops the empty area being dead: it is most
          of the control, and pointing at it should put the caret in the input,
          the way clicking the whitespace of any text box does. A chip's delete
          trigger bubbles through here too, which is harmless — focus lands
          where the next address would be typed anyway. */}
      <TagsInput.Control
        className={[
          'invite-recipients-control flex min-h-[136px] w-full flex-col gap-2 rounded-lg border p-2',
          // The error state's own border, per 11949:196271.
          rejected === null
            ? 'border-[var(--ds-color-border-primary)]'
            : 'border-[var(--ds-color-border-negative)]',
        ].join(' ')}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex flex-wrap content-start items-center gap-2">
          <TagsInput.Context>
            {(api) =>
              api.value.map((recipient, index) => (
                <TagsInput.Item index={index} key={recipient} value={recipient}>
                  {/* 12/6 and a gap of 4, per 11930:194479 — 12 on both
                      sides, not a narrower right side to sit the X closer to
                      the edge. */}
                  <TagsInput.ItemPreview className="flex items-center gap-1 rounded-[40px] bg-[var(--ds-color-bg-secondary-dark)] px-3 py-1.5 transition-colors duration-[120ms] ease-[ease] data-[highlighted]:bg-[var(--ds-color-bg-tertiary)] hover:bg-[var(--ds-color-bg-tertiary)]">
                    <TagsInput.ItemText className="text-[13px] leading-[18px] text-[var(--invite-chip-text)]">
                      {recipient}
                    </TagsInput.ItemText>
                    {/* Named per chip. "Remove" alone is what a list of six
                        identical buttons sounds like on a screen reader. */}
                    <TagsInput.ItemDeleteTrigger
                      aria-label={t('project-overview.invite-recipient-remove', {
                        recipient,
                      })}
                      className="invite-chip-remove flex size-4 shrink-0 cursor-pointer items-center justify-center text-[var(--invite-chip-text)]"
                    >
                      <X aria-hidden="true" className="size-4" strokeWidth={ICON_STROKE_WIDTH} />
                    </TagsInput.ItemDeleteTrigger>
                  </TagsInput.ItemPreview>
                </TagsInput.Item>
              ))
            }
          </TagsInput.Context>

          {/* flex-1 with a floor, so it shares the last chip's row when there is
              room for it and drops to its own when there is not.

              No vertical padding: 11949:196315 puts the placeholder at the
              box's 8px inset, and padding here pushed it 6 lower. Beside a
              chip the 20px input centres against the chip's 30, which is what
              the filled frame draws. The row's own height is the click
              target's only loss, and the control focuses the input from
              anywhere inside it. */}
          <TagsInput.Input
            className="min-w-[200px] flex-1 bg-transparent text-sm leading-5 text-[var(--ds-color-text-primary)] outline-none placeholder:text-[var(--ds-color-text-secondary)]"
            placeholder={t('project-overview.invite-recipients-placeholder')}
            ref={inputRef}
          />
        </div>
      </TagsInput.Control>

      {/* 11949:196271: the frame's `Icon / triangle-alert` at 16 beside the
          message, both in text-negative, 8 below the box — which is the Root's
          own gap.

          role=alert, because it answers something the user just did and there
          is nothing on screen to read it from: the rejected text is gone. The
          mark is decorative, since the sentence beside it is the message. */}
      {rejected !== null && (
        <p
          className="flex items-center gap-2 text-sm leading-5 text-[var(--ds-color-text-negative)]"
          role="alert"
        >
          <TriangleAlert
            aria-hidden="true"
            className="size-4 shrink-0"
            strokeWidth={ICON_STROKE_WIDTH}
          />
          {rejected}
        </p>
      )}
    </TagsInput.Root>
  );
};
